import { Order, OrderItem } from '@/eshop/gql/graphql'
import { k2CurrencyToCc, useK2Setup } from '@/eshop/hooks/useK2Setup'
import { langToLocale, useFormatPrice } from '@/eshop/utils/price'
import clsx from 'clsx'
import {
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react'
import { EshopContext } from '../EshopProvider'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid'
import { Button } from '@/components/Button'
import { getProductFromProductGroupName } from '@/eshop/utils/client'
import { BasketContext, BasketProduct } from '../BasketProvider'
import { useUser } from '@/eshop/hooks/useUser'
import { Option } from '@swan-io/boxed'
import { makeProductDetailUrl } from '@/eshop/utils/url'
import { toast } from 'react-toastify'
import OrderItemsTable from './OrderItemsTable'
import OrderState from './OrderState'

interface Props {
    className?: string
    order: Order
    showDetail?: boolean
    isOpen?: boolean
}
export default function UserOrder({
    className,
    order,
    showDetail,
    isOpen,
}: Props) {
    const { lang } = useContext(EshopContext)
    const { addProduct } = useContext(BasketContext)
    const user = useUser()
    const { t } = useTranslation()
    const k2 = useK2Setup()
    const [isAddingToBasket, setIsAddingToBasket] = useState(false)

    const [isExpanded, setIsExpanded] = useState(isOpen || false)

    const formatPrice = useFormatPrice(
        k2CurrencyToCc[order.currency!],
        '',
        true
    )

    const handleAddOrderToBasket = useCallback(() => {
        setIsAddingToBasket(true)
        async function run() {
            async function makeBasketItem(
                oi: OrderItem
            ): Promise<Option<BasketProduct>> {
                const product = await getProductFromProductGroupName(
                    oi.articleSortingNameLang!,
                    oi.articleId!,
                    k2.languageId
                )

                return product.match({
                    Some: (p) => {
                        if (!p.productGroup) {
                            return Option.None()
                        }
                        const pl = p.productLangItems.find(
                            (pl) => pl.languageId === k2.languageId
                        )
                        return Option.Some({
                            link: makeProductDetailUrl(
                                p.productGroup.sortingNameLang,
                                p.id
                            ),
                            currency: order.currency!,
                            name: oi.articleSortingNameLang!,
                            price: oi.invoiceUnitPriceNetCCalc,
                            priceVAT: oi.invoiceUnitPriceGrossCCalc,
                            id: p.id,
                            unit: oi.articleUnit!,
                            catNo: oi.articleAbbr!,
                            count: oi.quantityUMCalc!,
                            userId: user?.id || null,
                            goodsId: oi.articleId!,
                            manufacturer: pl?.brandName || '',
                            manufacturerId: p.brandRID || '',
                        })
                    },
                    None: () => Option.None(),
                })
            }

            let productsAdded = 0
            for (let oi of order?.orderItems || []) {
                if (!oi) {
                    return
                }
                ;(await makeBasketItem(oi)).match({
                    Some: (p) => {
                        productsAdded += 1
                        addProduct(p)
                    },
                    None: () => {
                        toast.error(
                            t(
                                'eshop.usersettings.orders.itemAddedToBasketError'
                            )
                        )
                    },
                })
            }

            if (productsAdded > 0) {
                toast.success(t('eshop.usersettings.orders.itemAddedToBasket'))
            }
            setIsAddingToBasket(false)
        }

        run()
    }, [
        addProduct,
        k2.languageId,
        order.currency,
        order?.orderItems,
        t,
        user?.id,
    ])

    const wrapper = useRef<HTMLDivElement>(null)
    useEffect(() => {
        if (isOpen && wrapper.current) {
            setTimeout(() => {
                wrapper.current?.scrollIntoView({
                    behavior: 'smooth',
                })
            }, 250)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <div className={clsx(className)} ref={wrapper}>
            <div className="text-lg font-bold">
                {formatOrderDate(order.issueDate as string, langToLocale[lang])}
            </div>

            <div className="border px-4 py-6 my-4 rounded">
                <div className="flex justify-between flex-col desktop:flex-row">
                    <div className="flex gap-6 justify-between">
                        <span>
                            {t('eshop.usersettings.orders.orderNo')}
                            {order.documentIdentificationCalc}
                        </span>
                        <OrderState
                            statusId={order.statusId!}
                            statusText={order.statusIdLanguageTextCalc!}
                        />
                    </div>

                    <div className="flex gap-6 justify-between">
                        <span>{formatPrice(order.amountNetC!, false)}</span>
                        <span>
                            {formatPrice(order.amountGrossCCalc!, true)}
                        </span>
                        {showDetail &&
                            (isExpanded ? (
                                <ChevronUpIcon
                                    onClick={() => setIsExpanded(false)}
                                    className="cursor-pointer"
                                    width={24}
                                    height={24}
                                />
                            ) : (
                                <ChevronDownIcon
                                    onClick={() => setIsExpanded(true)}
                                    className="cursor-pointer"
                                    width={24}
                                    height={24}
                                />
                            ))}
                    </div>
                </div>
                {isExpanded && (
                    <div
                        className={clsx(
                            'overflow-auto md:overflow-hidden py-10 text-md',
                            isExpanded ? 'max-h-full' : 'max-h-0'
                        )}
                    >
                        <OrderItemsTable
                            items={(order?.orderItems || []) as OrderItem[]}
                            currency={order.currency || undefined}
                        />
                    </div>
                )}
            </div>
            {order.orderItems && order.orderItems.length > 0 && (
                <div className="flex justify-between">
                    <div></div>
                    <Button
                        color="secondary"
                        onClick={handleAddOrderToBasket}
                        disabled={isAddingToBasket}
                    >
                        <InlineLoader
                            isLoading={isAddingToBasket}
                            className="mx-10"
                        >
                            {t('eshop.usersettings.orders.orderAgain')}
                        </InlineLoader>
                    </Button>
                </div>
            )}
        </div>
    )
}

export function formatOrderDate(date: string | Date, locale: string) {
    const d = new Date(date)

    return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    }).format(d)
}

export function InlineLoader({
    isLoading,
    children,
    className,
}: PropsWithChildren<{ isLoading: boolean; className?: string }>) {
    if (isLoading) {
        return (
            <div className={clsx('flex justify-center mx-4', className)}>
                <span className="loading loading-spinner loading-sm"></span>
            </div>
        )
    }
    return <>{children}</>
}
